/**
 * Pure decision helpers for the editor's cloud-file lifecycle in App.tsx.
 *
 * Each encodes an invariant that was the root cause of a real, silent data-loss
 * bug. Extracting them keeps that logic pinned by fast unit tests rather than
 * only by a full-app integration render, and keeps App.tsx's effects readable.
 */

import { CloudEditorFileStatus } from "./cloudEditorFile";

/** What the editor should do with a resolved cloud-load status. */
export type CloudLoadAction = "hydrate" | "notFound" | "retryableError";

/**
 * Map a load status to an editor action:
 *  - `ready`/`empty` → hydrate (an `empty` orphan self-heals into a blank canvas
 *    the user can draw into and save);
 *  - `error` → a RETRYABLE failure surface (never the 404 card — that lies to
 *    the owner about a transient blip). The caller must NOT mark the id hydrated
 *    on this path, so a retry / re-focus can re-attempt.
 *  - `notfound` → the uniform 404.
 */
export const cloudLoadAction = (
  status: CloudEditorFileStatus,
): CloudLoadAction => {
  switch (status) {
    case "ready":
    case "empty":
      return "hydrate";
    case "error":
      return "retryableError";
    case "notfound":
    default:
      return "notFound";
  }
};

/**
 * Hydrate a cloud file's scene into the editor at most once per file id.
 *
 * The hydration effect re-runs whenever the Supabase access token changes (the
 * ~hourly TOKEN_REFRESHED, plus the first auth resolution). Re-fetching and
 * re-applying the server scene for an already-open file overwrites the user's
 * live edits — and marking that stale scene "synced" then swallows the pending
 * autosave, losing the edits for good. So only hydrate when the id last
 * hydrated differs from the one now in the URL. A FAILED / skipped hydration
 * must NOT be recorded (so it retries once auth is ready): callers set
 * `hydratedFileId` only on a successful apply.
 */
export const shouldHydrateCloudFile = ({
  currentFileId,
  hydratedFileId,
}: {
  currentFileId: string | null;
  hydratedFileId: string | null;
}): boolean => currentFileId !== null && hydratedFileId !== currentFileId;

/**
 * Whether a focus/visibility multi-tab sync may replace the editor scene with
 * the anonymous scratch canvas read from localStorage.
 *
 * This upstream multi-tab sync predates cloud files: on refocus it splats
 * `importFromLocalStorage()` over the editor. With a cloud file open that
 * destroys the file in the editor within minutes of a tab switch — and cloud
 * autosave then persists the corruption to the server. The scratch scene may
 * only be applied on the scratch canvas.
 */
export const shouldRestoreBrowserScene = ({
  isCloudContext,
  browserStateNewer,
}: {
  isCloudContext: boolean;
  browserStateNewer: boolean;
}): boolean => !isCloudContext && browserStateNewer;

/**
 * Offer the "save your work" login handoff once: signed in, scene has content,
 * on the scratch canvas, not previously dismissed. Sampled on content changes
 * (not only at the login instant) so drawing AFTER signing in still offers it.
 */
/**
 * Should landing on `/` put the editor straight into create-on-first-edit?
 *
 * Signed in = always a file. ☰ → New mindmap and My Files → `/?new=1` already
 * enter that state; arriving on `/` did not, so the first stroke there saved
 * nothing. Two deliberate exclusions:
 *  - PRE-EXISTING scratch content is left alone. Converting it silently would
 *    mint surprise files, and would override a "Not now" the user may already
 *    have given the save prompt — that content keeps the prompt instead.
 *  - `scratchHasContent` is read from the STORED scratch scene, not from the
 *    editor: the editor's contents are only knowable once its initial data has
 *    been applied, which races this effect (same ordering as `/?new=1`).
 */
export const shouldEnterCreateOnFirstEdit = ({
  hasToken,
  authResolved,
  editorReady,
  isCloudContext,
  isScratchCanvas,
  scratchHasContent,
}: {
  hasToken: boolean;
  authResolved: boolean;
  editorReady: boolean;
  isCloudContext: boolean;
  isScratchCanvas: boolean;
  scratchHasContent: boolean;
}): boolean =>
  hasToken &&
  authResolved &&
  editorReady &&
  isScratchCanvas &&
  !isCloudContext &&
  !scratchHasContent;

export const shouldOfferSaveWorkPrompt = ({
  hasToken,
  authResolved,
  isCloudContext,
  dismissed,
  hasContent,
}: {
  hasToken: boolean;
  authResolved: boolean;
  isCloudContext: boolean;
  dismissed: boolean;
  hasContent: boolean;
}): boolean =>
  hasToken && authResolved && !isCloudContext && !dismissed && hasContent;
